/**
 * SymptomsPage - Dedicated Symptoms & Concerns Management
 * 
 * Features:
 * - Active symptoms (still happening)
 * - Recent symptoms (resolved)
 * - Timeline view of all entries
 * - Add to appointment functionality
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Plus, 
  Thermometer, 
  Calendar,
  Edit3,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarPlus,
  X,
  Loader2
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 30%, #FEF9C3 60%, #F0FDF4 100%)',
};

const SYMPTOM_CATEGORIES = [
  { value: 'pain', label: 'Pain' },
  { value: 'digestive', label: 'Digestive' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'skin', label: 'Skin' },
  { value: 'headache', label: 'Headache' },
  { value: 'dizziness', label: 'Dizziness' },
  { value: 'menstrual', label: 'Menstrual / Reproductive' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'mood', label: 'Mood / Energy' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: 'mild', label: 'Mild', color: 'bg-green-100 text-green-700' },
  { value: 'moderate', label: 'Moderate', color: 'bg-amber-100 text-amber-700' },
  { value: 'severe', label: 'Severe', color: 'bg-red-100 text-red-700' },
];

const FREQUENCY_OPTIONS = [
  { value: 'constant', label: 'Constant' },
  { value: 'frequent', label: 'Frequent (multiple times daily)' },
  { value: 'occasional', label: 'Occasional (few times a week)' },
  { value: 'rare', label: 'Rare (once a week or less)' },
];

const SymptomsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDemoMode, demoData } = useDemo();
  
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [appointments, setAppointments] = useState([]);
  const [showAddToApptDialog, setShowAddToApptDialog] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    stillHappening: true,
    severity: '',
    frequency: '',
    description: '',
    bodyLocation: '',
    triggers: '',
    whatHelps: '',
    whatWorsens: '',
    notes: '',
  });

  useEffect(() => {
    fetchSymptoms();
    fetchAppointments();
  }, [isDemoMode]);

  const fetchSymptoms = async () => {
    if (isDemoMode) {
      setSymptoms(demoData?.medical?.symptoms || []);
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/medical/symptoms');
      setSymptoms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch symptoms:', error);
      toast.error('Failed to load symptoms');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    if (isDemoMode) return;
    
    try {
      const response = await API.get('/medical/appointment-preps?status=draft');
      setAppointments(response.data?.filter(a => a.status !== 'completed') || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      stillHappening: true,
      severity: '',
      frequency: '',
      description: '',
      bodyLocation: '',
      triggers: '',
      whatHelps: '',
      whatWorsens: '',
      notes: '',
    });
    setEditingSymptom(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleOpenEdit = (symptom) => {
    setFormData({
      name: symptom.name || '',
      category: symptom.category || '',
      startDate: symptom.startDate || symptom.start_date || format(new Date(), 'yyyy-MM-dd'),
      endDate: symptom.endDate || symptom.end_date || '',
      stillHappening: !symptom.resolved && !symptom.endDate && !symptom.end_date,
      severity: symptom.severity || '',
      frequency: symptom.frequency || '',
      description: symptom.description || '',
      bodyLocation: symptom.bodyLocation || symptom.body_location || '',
      triggers: symptom.triggers || '',
      whatHelps: symptom.whatHelps || symptom.what_helps || '',
      whatWorsens: symptom.whatWorsens || symptom.what_worsens || '',
      notes: symptom.notes || '',
    });
    setEditingSymptom(symptom);
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a symptom name');
      return;
    }

    if (isDemoMode) {
      toast.success(editingSymptom ? 'Symptom updated (demo)' : 'Symptom added (demo)');
      setShowAddDialog(false);
      resetForm();
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        name: formData.name,
        category: formData.category,
        start_date: formData.startDate,
        end_date: formData.stillHappening ? null : formData.endDate || null,
        resolved: !formData.stillHappening,
        severity: formData.severity,
        frequency: formData.frequency,
        description: formData.description,
        body_location: formData.bodyLocation,
        triggers: formData.triggers,
        what_helps: formData.whatHelps,
        what_worsens: formData.whatWorsens,
        notes: formData.notes,
      };

      if (editingSymptom) {
        await API.put(`/medical/symptoms/${editingSymptom.id}`, dataToSave);
        toast.success('Symptom updated');
      } else {
        await API.post('/medical/symptoms', dataToSave);
        toast.success('Symptom added');
      }
      
      fetchSymptoms();
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save symptom');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (symptom) => {
    if (isDemoMode) {
      toast.success('Symptom deleted (demo)');
      setDeleteConfirm(null);
      return;
    }

    try {
      await API.delete(`/medical/symptoms/${symptom.id}`);
      toast.success('Symptom deleted');
      fetchSymptoms();
    } catch (error) {
      toast.error('Failed to delete symptom');
    }
    setDeleteConfirm(null);
  };

  const handleMarkResolved = async (symptom) => {
    if (isDemoMode) {
      toast.success('Symptom marked resolved (demo)');
      return;
    }

    try {
      await API.put(`/medical/symptoms/${symptom.id}`, {
        ...symptom,
        resolved: true,
        end_date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast.success('Symptom marked as resolved');
      fetchSymptoms();
    } catch (error) {
      toast.error('Failed to update symptom');
    }
  };

  const handleAddToAppointment = async (symptom, appointmentId) => {
    if (isDemoMode) {
      toast.success('Added to appointment (demo)');
      setShowAddToApptDialog(null);
      return;
    }

    try {
      // Get current appointment
      const apptRes = await API.get(`/medical/appointment-preps/${appointmentId}`);
      const appointment = apptRes.data;
      
      // Add symptom to appointment
      const updatedSymptoms = [...(appointment.symptoms || []), {
        id: symptom.id,
        text: symptom.name,
        startDate: symptom.startDate || symptom.start_date,
        severity: symptom.severity,
        fromSymptomLog: true,
      }];
      
      await API.put(`/medical/appointment-preps/${appointmentId}`, {
        symptoms: updatedSymptoms,
      });
      
      toast.success('Added to appointment');
      setShowAddToApptDialog(null);
    } catch (error) {
      toast.error('Failed to add to appointment');
    }
  };

  // Filter symptoms
  const activeSymptoms = symptoms.filter(s => !s.resolved && !s.endDate && !s.end_date);
  const recentSymptoms = symptoms.filter(s => s.resolved || s.endDate || s.end_date);

  const getSeverityBadge = (severity) => {
    const option = SEVERITY_OPTIONS.find(o => o.value === severity);
    if (!option) return null;
    return (
      <Badge variant="outline" className={`text-xs ${option.color}`}>
        {option.label}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const cat = SYMPTOM_CATEGORIES.find(c => c.value === category);
    return cat?.label || category;
  };

  const renderSymptomCard = (symptom) => (
    <Card key={symptom.id} className="mb-3" data-testid={`symptom-${symptom.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            symptom.resolved || symptom.endDate || symptom.end_date 
              ? 'bg-slate-100' 
              : 'bg-orange-100'
          }`}>
            <Thermometer className={`h-5 w-5 ${
              symptom.resolved || symptom.endDate || symptom.end_date 
                ? 'text-slate-500' 
                : 'text-orange-600'
            }`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-medium text-slate-800">{symptom.name}</h3>
              {getSeverityBadge(symptom.severity)}
              {symptom.category && (
                <Badge variant="secondary" className="text-xs">
                  {getCategoryLabel(symptom.category)}
                </Badge>
              )}
            </div>
            
            <div className="text-xs text-slate-500 space-y-0.5">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Started {symptom.startDate || symptom.start_date 
                  ? format(parseISO(symptom.startDate || symptom.start_date), 'MMM d, yyyy')
                  : 'Unknown'}
                {(symptom.endDate || symptom.end_date) && (
                  <span> — Resolved {format(parseISO(symptom.endDate || symptom.end_date), 'MMM d, yyyy')}</span>
                )}
              </div>
              {symptom.frequency && (
                <div>Frequency: {FREQUENCY_OPTIONS.find(f => f.value === symptom.frequency)?.label || symptom.frequency}</div>
              )}
              {symptom.bodyLocation && (
                <div>Location: {symptom.bodyLocation || symptom.body_location}</div>
              )}
            </div>
            
            {symptom.description && (
              <p className="text-sm text-slate-600 mt-2">{symptom.description}</p>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenEdit(symptom)}
                className="text-xs h-7"
              >
                <Edit3 className="h-3 w-3 mr-1" />
                Edit
              </Button>
              
              {!symptom.resolved && !symptom.endDate && !symptom.end_date && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMarkResolved(symptom)}
                    className="text-xs h-7 text-green-600 hover:text-green-700"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Mark Resolved
                  </Button>
                  
                  {appointments.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddToApptDialog(symptom)}
                      className="text-xs h-7 text-blue-600 hover:text-blue-700"
                    >
                      <CalendarPlus className="h-3 w-3 mr-1" />
                      Add to Appointment
                    </Button>
                  )}
                </>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirm(symptom)}
                className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="symptoms-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-orange-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/medical')}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Thermometer className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Symptoms & Concerns</h1>
              <p className="text-xs text-slate-500">Track how you&apos;re feeling over time</p>
            </div>
            <Button
              size="sm"
              onClick={handleOpenAdd}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="active" className="text-sm">
              Active ({activeSymptoms.length})
            </TabsTrigger>
            <TabsTrigger value="recent" className="text-sm">
              Resolved ({recentSymptoms.length})
            </TabsTrigger>
            <TabsTrigger value="timeline" className="text-sm">
              Timeline
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {activeSymptoms.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="font-medium text-slate-800 mb-2">No active symptoms</h3>
                <p className="text-sm text-slate-500 mb-4">Track any health changes you&apos;re experiencing</p>
                <Button onClick={handleOpenAdd} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Symptom
                </Button>
              </div>
            ) : (
              activeSymptoms.map(renderSymptomCard)
            )}
          </TabsContent>

          <TabsContent value="recent">
            {recentSymptoms.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <p className="text-sm text-slate-500">No resolved symptoms yet</p>
              </div>
            ) : (
              recentSymptoms.map(renderSymptomCard)
            )}
          </TabsContent>

          <TabsContent value="timeline">
            {symptoms.length === 0 ? (
              <div className="text-center py-12 bg-white/50 rounded-lg">
                <p className="text-sm text-slate-500">No symptoms recorded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {symptoms
                  .sort((a, b) => {
                    const dateA = parseISO(a.startDate || a.start_date || '2000-01-01');
                    const dateB = parseISO(b.startDate || b.start_date || '2000-01-01');
                    return dateB - dateA;
                  })
                  .map(renderSymptomCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && setShowAddDialog(false)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSymptom ? 'Edit Symptom' : 'Add Symptom'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Symptom or Event *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Headache, Back pain, Period started"
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {SYMPTOM_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  disabled={formData.stillHappening}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="stillHappening">Still happening</Label>
              <Switch
                id="stillHappening"
                checked={formData.stillHappening}
                onCheckedChange={(v) => setFormData({ ...formData, stillHappening: v })}
              />
            </div>

            <div>
              <Label htmlFor="severity">Severity</Label>
              <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select severity (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="How often? (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what you're experiencing"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="bodyLocation">Body Location</Label>
              <Input
                id="bodyLocation"
                value={formData.bodyLocation}
                onChange={(e) => setFormData({ ...formData, bodyLocation: e.target.value })}
                placeholder="e.g., Lower back, Right side"
              />
            </div>

            <div>
              <Label htmlFor="triggers">Possible Triggers</Label>
              <Input
                id="triggers"
                value={formData.triggers}
                onChange={(e) => setFormData({ ...formData, triggers: e.target.value })}
                placeholder="What might cause this?"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="whatHelps">What Helps</Label>
                <Input
                  id="whatHelps"
                  value={formData.whatHelps}
                  onChange={(e) => setFormData({ ...formData, whatHelps: e.target.value })}
                  placeholder="What makes it better?"
                />
              </div>
              <div>
                <Label htmlFor="whatWorsens">What Worsens</Label>
                <Input
                  id="whatWorsens"
                  value={formData.whatWorsens}
                  onChange={(e) => setFormData({ ...formData, whatWorsens: e.target.value })}
                  placeholder="What makes it worse?"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any other details"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingSymptom ? 'Update' : 'Add'} Symptom
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Symptom</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add to Appointment Dialog */}
      <Dialog open={!!showAddToApptDialog} onOpenChange={() => setShowAddToApptDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to Appointment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Select an appointment to add &quot;{showAddToApptDialog?.name}&quot; to:
            </p>
            <div className="space-y-2">
              {appointments.map(appt => (
                <Card 
                  key={appt.id}
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleAddToAppointment(showAddToApptDialog, appt.id)}
                >
                  <CardContent className="p-3">
                    <div className="font-medium text-sm text-slate-800">
                      {appt.doctorName || 'Appointment'}
                    </div>
                    <div className="text-xs text-slate-500">
                      {appt.appointmentDate && format(parseISO(appt.appointmentDate), 'MMM d, yyyy')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToApptDialog(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SymptomsPage;
